import React from "react";
import {
  Radio,
  Form,
  AutoComplete,
  DatePicker,
  Select,
  Input,
  Button,
} from "antd";
import styles from "./index.module.css";
import moment from "moment";
import { airports } from "./airports";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

export interface TravelWidgetProps {}

enum TripType {
  ONE_WAY = "ONE_WAY",
  ROUND_TRIP = "ROUND_TRIP",
}

enum TripClass {
  ECONOMY = "ECONOMY",
  BUSINESS = "BUSINESS",
  FIRST = "FIRST",
}

export const TravelWidget: React.FC<TravelWidgetProps> = () => {
  const [formValue, setFormValue] = React.useState<any>({
    tripType: TripType.ROUND_TRIP,
    adults: 1,
    children: 0,
    class: TripClass.ECONOMY,
    departureDate: moment(),
    returnDate: moment().add(2, "days"),
    from: "",
    to: "",
  });

  const [errors, setErrors] = React.useState<any>({});

  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const countOptions = [
    { value: 0 },
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
    { value: 6 },
    { value: 7 },
    { value: 8 },
    { value: 9 },
  ];

  const classOptions = [
    { value: TripClass.ECONOMY, label: "Economy" },
    { value: TripClass.BUSINESS, label: "Business" },
    { value: TripClass.FIRST, label: "First" },
  ];

  const disabledDate = (current: any) => {
    // Can not select days before today and today
    return current && current < moment().startOf("day");
  };

  const onSubmit = async () => {
    const errors: any = {};
    if (!formValue.name) {
      errors.name = "Required";
    }

    if (!formValue.phone) {
      errors.phone = "Required";
    }

    if (!formValue.email) {
      errors.email = "Required";
    }

    if (JSON.stringify(errors) !== "{}") {
      setErrors(errors);
      return;
    }

    await fetch("https://heuristic.simpleforms.ml/travel-form", {
      method: "POST",
      body: JSON.stringify(formValue),
      headers: {
        "Content-Type": "application/json",
      },
    });

    setIsSubmitted(true);
  };

  React.useEffect(() => {
    document
      .querySelectorAll(".ant-select-selection-search-input input")
      .forEach((e) => {
        e.setAttribute("autocomplete", "chrome-off");
      });
  }, []);

  React.useEffect(() => {
    if (JSON.stringify(errors) !== "{}") {
      setErrors({});
    }
  }, [formValue]);

  return (
    <div className={styles.container}>
      {isSubmitted ? (
        <div className={styles.thanks}>
          Thank You. We'll Contact you shortly to help you plan your next
          flight.
        </div>
      ) : (
        <Form layout="vertical">
          <Form.Item>
            <Radio.Group
              value={formValue.tripType}
              onChange={(e) =>
                setFormValue((value: any) => ({
                  ...value,
                  tripType: e.target.value,
                }))
              }
            >
              <Radio value={TripType.ROUND_TRIP}>Round Trip</Radio>
              <Radio value={TripType.ONE_WAY}>One Way</Radio>
            </Radio.Group>
          </Form.Item>
          <div className={`${styles.row} ${styles.col2}`}>
            <Form.Item label="Flying From" required>
              <AutoComplete
                options={airports}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                onChange={(val) =>
                  setFormValue((form: any) => ({ ...form, from: val }))
                }
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  size="small"
                  placeholder="Enter a City or Airport"
                  className="ant-select-selection-search-input"
                />
              </AutoComplete>
            </Form.Item>
            <Form.Item label="Flying To" required>
              <AutoComplete
                options={airports}
                filterOption={(inputValue, option) =>
                  option!.value
                    .toUpperCase()
                    .indexOf(inputValue.toUpperCase()) !== -1
                }
                onChange={(val) =>
                  setFormValue((form: any) => ({ ...form, to: val }))
                }
              >
                <Input
                  prefix={<EnvironmentOutlined />}
                  size="small"
                  placeholder="Enter a City or Airport"
                  className="ant-select-selection-search-input"
                />
              </AutoComplete>
            </Form.Item>
          </div>
          <div className={`${styles.row} ${styles.col2}`}>
            <div className={`${styles.rowmobile} ${styles.col2}`}>
              <Form.Item label="Departure Date" required>
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                  value={formValue.departureDate}
                  onChange={(val) =>
                    setFormValue(
                      (form: any) => ({ ...form, departureDate: val } as any)
                    )
                  }
                />
              </Form.Item>
              <Form.Item label="Return Date" required>
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                  onChange={(val) =>
                    setFormValue(
                      (form: any) => ({ ...form, returnDate: val } as any)
                    )
                  }
                  value={formValue.returnDate}
                />
              </Form.Item>
            </div>
            <div className={`${styles.rowmobile} ${styles.col3}`}>
              <Form.Item label="Adults">
                <Select
                  options={countOptions}
                  value={formValue.adults}
                  onChange={(adults) =>
                    setFormValue((val: any) => ({ ...val, adults }))
                  }
                />
              </Form.Item>
              <Form.Item label="Children">
                <Select
                  options={countOptions}
                  value={formValue.children}
                  onChange={(children) =>
                    setFormValue((val: any) => ({ ...val, children }))
                  }
                />
              </Form.Item>
              <Form.Item label="Class">
                <Select
                  options={classOptions}
                  value={formValue.class}
                  onChange={(cls) =>
                    setFormValue((val: any) => ({ ...val, class: cls }))
                  }
                />
              </Form.Item>
            </div>
          </div>
          <div className={`${styles.row} ${styles.col3}`}>
            <Form.Item
              label="Name"
              required
              validateStatus={errors.name ? "error" : undefined}
              help={errors.name}
            >
              <Input
                name="name"
                required
                size="small"
                onChange={(e) =>
                  setFormValue((form: any) => ({
                    ...form,
                    name: e.currentTarget.value,
                  }))
                }
                autoComplete="off"
                prefix={<UserOutlined />}
              />
            </Form.Item>
            <Form.Item
              label="Email"
              required
              validateStatus={errors.email ? "error" : undefined}
              help={errors.email}
            >
              <Input
                name="email"
                required
                type="email"
                onChange={(e) =>
                  setFormValue((form: any) => ({
                    ...form,
                    email: e.currentTarget.value,
                  }))
                }
                autoComplete="off"
                prefix={<MailOutlined />}
                size="small"
              />
            </Form.Item>
            <Form.Item
              label="Contact No."
              required
              validateStatus={errors.phone ? "error" : undefined}
              help={errors.phone}
            >
              <Input
                name="phone"
                required
                onChange={(e) =>
                  setFormValue((form: any) => ({
                    ...form,
                    phone: e.currentTarget.value,
                  }))
                }
                type="tel"
                autoComplete="off"
                prefix={<PhoneOutlined />}
                size="small"
              />
            </Form.Item>
          </div>
          <div className={styles.submit}>
            <Button type="primary" onClick={onSubmit}>
              Submit
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};
